import { getCurrentPosition, isNativeApp } from "../native/runtime";

const GEO_PERMISSION_DENIED = 1;
const GEO_POSITION_UNAVAILABLE = 2;
const GEO_TIMEOUT = 3;
const TELEGRAM_LOCATION_MIN_VERSION = "8.0";
export const ATTENDANCE_LOCATION_MAX_AGE_MS = 20000;
export const ATTENDANCE_LOCATION_MAX_ACCURACY_M = 100;
const ATTENDANCE_LOCATION_QUICK_TIMEOUT_MS = 1000;

function createGeoError(issue, options = {}) {
  const error = new Error(options.message || issue);
  error.issue = issue;
  if (options.code !== undefined) error.code = options.code;
  if (options.source) error.source = options.source;
  if (options.permissionState) error.permissionState = options.permissionState;
  if (options.env) error.env = options.env;
  if (options.originalError) error.originalError = options.originalError;
  return error;
}

function getNavigatorObject() {
  return typeof navigator !== "undefined" ? navigator : null;
}

function getWindowObject() {
  return typeof window !== "undefined" ? window : null;
}

function getTelegramWebApp() {
  return getWindowObject()?.Telegram?.WebApp || null;
}

function normalizePositionTimestamp(position) {
  const timestamp = Number(position?.timestamp);
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : Date.now();
}

function telegramLocationSupported(webApp) {
  if (!webApp?.LocationManager) return false;
  if (typeof webApp.isVersionAtLeast !== "function") return true;
  try {
    return webApp.isVersionAtLeast(TELEGRAM_LOCATION_MIN_VERSION);
  } catch {
    return false;
  }
}

function normalizeBrowserPosition(position, env, { isCached = false } = {}) {
  return {
    latitude: Number(position.coords.latitude),
    longitude: Number(position.coords.longitude),
    accuracy:
      typeof position.coords.accuracy === "number"
        ? position.coords.accuracy
        : null,
    timestamp: normalizePositionTimestamp(position),
    isCached,
    source: "browser",
    env,
  };
}

function normalizeNativePosition(position, env, { isCached = false } = {}) {
  return {
    latitude: Number(position.coords.latitude),
    longitude: Number(position.coords.longitude),
    accuracy:
      typeof position.coords.accuracy === "number"
        ? position.coords.accuracy
        : null,
    timestamp: normalizePositionTimestamp(position),
    isCached,
    source: "native",
    env,
  };
}

function normalizeTelegramPosition(locationData, env) {
  return {
    latitude: Number(locationData.latitude),
    longitude: Number(locationData.longitude),
    accuracy:
      typeof locationData.horizontal_accuracy === "number"
        ? locationData.horizontal_accuracy
        : null,
    timestamp: Date.now(),
    isCached: false,
    source: "telegram",
    env,
  };
}

export function isAcceptableAttendancePosition(position, options = {}) {
  const latitude = Number(position?.latitude);
  const longitude = Number(position?.longitude);
  const accuracy = Number(position?.accuracy);
  const timestamp = Number(position?.timestamp);
  const nowMs = Number(options.nowMs ?? Date.now());
  const maxAgeMs = Number(
    options.maxAgeMs ?? ATTENDANCE_LOCATION_MAX_AGE_MS
  );
  const maxAccuracyM = Number(
    options.maxAccuracyM ?? ATTENDANCE_LOCATION_MAX_ACCURACY_M
  );
  const ageMs = nowMs - timestamp;

  return (
    Number.isFinite(latitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    Number.isFinite(longitude) &&
    longitude >= -180 &&
    longitude <= 180 &&
    position?.accuracy != null &&
    Number.isFinite(accuracy) &&
    accuracy >= 0 &&
    accuracy <= maxAccuracyM &&
    position?.timestamp != null &&
    Number.isFinite(timestamp) &&
    ageMs >= -5000 &&
    ageMs <= maxAgeMs
  );
}

function distanceBetweenCoordinatesM(lat1, lon1, lat2, lon2) {
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const earthRadiusM = 6371000;
  const deltaLatitude = toRadians(lat2 - lat1);
  const deltaLongitude = toRadians(lon2 - lon1);
  const a =
    Math.sin(deltaLatitude / 2) ** 2 +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(deltaLongitude / 2) ** 2;
  return earthRadiusM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function isReusableAttendancePosition(position, eventData, options = {}) {
  if (!isAcceptableAttendancePosition(position, options)) return false;
  if (!position?.isCached) return true;

  const eventLatitude = Number(eventData?.latitude);
  const eventLongitude = Number(eventData?.longitude);
  const allowedRadiusM = Number(eventData?.allowed_radius);
  if (
    !Number.isFinite(eventLatitude) ||
    !Number.isFinite(eventLongitude) ||
    !Number.isFinite(allowedRadiusM) ||
    allowedRadiusM <= 0
  ) return false;

  const distanceM = distanceBetweenCoordinatesM(
    Number(position.latitude),
    Number(position.longitude),
    eventLatitude,
    eventLongitude
  );
  return distanceM + Number(position.accuracy) <= allowedRadiusM;
}

function requestBrowserPosition(options) {
  return new Promise((resolve, reject) => {
    const nav = getNavigatorObject();
    if (!nav?.geolocation) {
      reject(createGeoError("not_supported", { source: "browser" }));
      return;
    }
    nav.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

function watchBrowserPosition({
  timeoutMs = 12000,
  minAccuracyM = 120,
  minWaitMs = 4000,
  env,
}) {
  return new Promise((resolve, reject) => {
    const nav = getNavigatorObject();
    if (!nav?.geolocation) {
      reject(createGeoError("not_supported", { source: "browser", env }));
      return;
    }

    let done = false;
    let watchId = null;
    let timerId = null;
    const startedAt = Date.now();

    const settle = (ok, value) => {
      if (done) return;
      done = true;
      if (watchId != null) nav.geolocation.clearWatch(watchId);
      if (timerId != null) clearTimeout(timerId);
      if (ok) resolve(value);
      else reject(value);
    };

    watchId = nav.geolocation.watchPosition(
      (position) => {
        const accuracy =
          typeof position.coords.accuracy === "number"
            ? position.coords.accuracy
            : Number.POSITIVE_INFINITY;
        const waitedLongEnough = Date.now() - startedAt >= minWaitMs;
        if (accuracy <= minAccuracyM || waitedLongEnough) {
          settle(true, normalizeBrowserPosition(position, env));
        }
      },
      (error) => {
        settle(
          false,
          createGeoError(getGeolocationErrorKind(error), {
            code: error?.code,
            source: "browser",
            env,
            originalError: error,
          })
        );
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: timeoutMs,
      }
    );

    timerId = setTimeout(() => {
      settle(
        false,
        createGeoError("timeout", {
          code: GEO_TIMEOUT,
          source: "browser",
          env,
        })
      );
    }, timeoutMs + 1000);
  });
}

async function getBrowserPermissionState() {
  const nav = getNavigatorObject();
  if (!nav?.permissions?.query) return "unknown";
  try {
    const result = await nav.permissions.query({ name: "geolocation" });
    return result?.state || "unknown";
  } catch {
    return "unknown";
  }
}

async function requestBrowserLocation({
  cacheMaxAgeMs = ATTENDANCE_LOCATION_MAX_AGE_MS,
  quickTimeoutMs = ATTENDANCE_LOCATION_QUICK_TIMEOUT_MS,
  preciseTimeoutMs = 10000,
  watchTimeoutMs = 12000,
  maxAcceptableAccuracyM = ATTENDANCE_LOCATION_MAX_ACCURACY_M,
  env,
} = {}) {
  const permissionState = await getBrowserPermissionState();

  if (!env.hasBrowserGeolocation) {
    throw createGeoError("not_supported", {
      source: "browser",
      permissionState,
      env,
    });
  }

  if (!env.isSecureContext) {
    throw createGeoError("insecure_context", {
      code: "insecure_context",
      source: "browser",
      permissionState,
      env,
    });
  }

  if (cacheMaxAgeMs > 0) {
    try {
      const cachedPosition = await requestBrowserPosition({
        enableHighAccuracy: false,
        maximumAge: cacheMaxAgeMs,
        timeout: quickTimeoutMs,
      });
      const normalizedCachedPosition = normalizeBrowserPosition(
        cachedPosition,
        env,
        { isCached: true }
      );
      if (
        isAcceptableAttendancePosition(normalizedCachedPosition, {
          maxAgeMs: cacheMaxAgeMs,
          maxAccuracyM: maxAcceptableAccuracyM,
        })
      ) {
        return normalizedCachedPosition;
      }
    } catch (cachedError) {
      if (cachedError?.code === GEO_PERMISSION_DENIED) {
        throw createGeoError("permission_denied", {
          code: GEO_PERMISSION_DENIED,
          source: "browser",
          permissionState,
          env,
          originalError: cachedError,
        });
      }
    }
  }

  try {
    const precisePosition = await requestBrowserPosition({
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: preciseTimeoutMs,
    });
    return normalizeBrowserPosition(precisePosition, env);
  } catch (preciseError) {
    if (preciseError?.code === GEO_PERMISSION_DENIED) {
      throw createGeoError("permission_denied", {
        code: GEO_PERMISSION_DENIED,
        source: "browser",
        permissionState,
        env,
        originalError: preciseError,
      });
    }

    try {
      return await watchBrowserPosition({ timeoutMs: watchTimeoutMs, env });
    } catch (watchError) {
      if (watchError?.issue) {
        watchError.permissionState ||= permissionState;
        throw watchError;
      }

      throw createGeoError(getGeolocationErrorKind(preciseError), {
        code: preciseError?.code,
        source: "browser",
        permissionState,
        env,
        originalError: preciseError,
      });
    }
  }
}

function initTelegramLocationManager(manager) {
  return new Promise((resolve) => {
    let resolved = false;

    const finish = () => {
      if (resolved) return;
      resolved = true;
      resolve();
    };

    try {
      manager.init(finish);
      setTimeout(finish, 1500);
    } catch {
      finish();
    }
  });
}

async function requestTelegramLocation({ timeoutMs = 12000, env } = {}) {
  const webApp = getTelegramWebApp();
  const manager = webApp?.LocationManager;

  if (!telegramLocationSupported(webApp) || !manager) {
    throw createGeoError("not_supported", {
      code: "telegram_not_supported",
      source: "telegram",
      env,
    });
  }

  await initTelegramLocationManager(manager);

  if (!manager.isLocationAvailable) {
    throw createGeoError("telegram_unavailable", {
      code: "telegram_unavailable",
      source: "telegram",
      permissionState: manager.isAccessRequested
        ? manager.isAccessGranted
          ? "granted"
          : "denied"
        : "prompt",
      env,
    });
  }

  return new Promise((resolve, reject) => {
    let done = false;
    const timerId = setTimeout(() => {
      if (done) return;
      done = true;
      reject(
        createGeoError("timeout", {
          code: "telegram_timeout",
          source: "telegram",
          permissionState: manager.isAccessRequested
            ? manager.isAccessGranted
              ? "granted"
              : "denied"
            : "prompt",
          env,
        })
      );
    }, timeoutMs + 1000);

    try {
      manager.getLocation((locationData) => {
        if (done) return;
        done = true;
        clearTimeout(timerId);

        if (
          locationData &&
          Number.isFinite(Number(locationData.latitude)) &&
          Number.isFinite(Number(locationData.longitude))
        ) {
          resolve(normalizeTelegramPosition(locationData, env));
          return;
        }

        reject(
          createGeoError(
            manager.isAccessRequested && !manager.isAccessGranted
              ? "telegram_permission_denied"
              : "telegram_unavailable",
            {
              code:
                manager.isAccessRequested && !manager.isAccessGranted
                  ? "telegram_permission_denied"
                  : "telegram_unavailable",
              source: "telegram",
              permissionState: manager.isAccessRequested
                ? manager.isAccessGranted
                  ? "granted"
                  : "denied"
                : "prompt",
              env,
            }
          )
        );
      });
    } catch (error) {
      clearTimeout(timerId);
      reject(
        createGeoError("telegram_unavailable", {
          code: "telegram_unavailable",
          source: "telegram",
          permissionState: manager.isAccessRequested
            ? manager.isAccessGranted
              ? "granted"
              : "denied"
            : "prompt",
          env,
          originalError: error,
        })
      );
    }
  });
}

export function getGeolocationEnvironment() {
  const nav = getNavigatorObject();
  const win = getWindowObject();
  const tgWebApp = getTelegramWebApp();

  let displayModeStandalone = false;
  try {
    displayModeStandalone = !!win?.matchMedia?.("(display-mode: standalone)")
      ?.matches;
  } catch {
    displayModeStandalone = false;
  }

  return {
    isNativeApp: isNativeApp(),
    hasBrowserGeolocation: isNativeApp() || Boolean(nav?.geolocation),
    hasPermissionsApi: Boolean(nav?.permissions?.query),
    isSecureContext: isNativeApp() || Boolean(win?.isSecureContext ?? false),
    isStandalone: Boolean(nav?.standalone) || displayModeStandalone,
    isTelegramWebApp: Boolean(tgWebApp),
    hasTelegramLocationManager: telegramLocationSupported(tgWebApp),
  };
}

export async function requestBestEffortGeolocation(options = {}) {
  const env = getGeolocationEnvironment();

  if (env.isNativeApp) {
    const cacheMaxAgeMs = Number(
      options.cacheMaxAgeMs ?? ATTENDANCE_LOCATION_MAX_AGE_MS
    );
    const maxAcceptableAccuracyM = Number(
      options.maxAcceptableAccuracyM ??
        ATTENDANCE_LOCATION_MAX_ACCURACY_M
    );

    if (cacheMaxAgeMs > 0) {
      try {
        const cachedPosition = normalizeNativePosition(
          await getCurrentPosition({
            enableHighAccuracy: false,
            maximumAge: cacheMaxAgeMs,
            timeout: Number(
              options.quickTimeoutMs || ATTENDANCE_LOCATION_QUICK_TIMEOUT_MS
            ),
          }),
          env,
          { isCached: true }
        );
        if (
          isAcceptableAttendancePosition(cachedPosition, {
            maxAgeMs: cacheMaxAgeMs,
            maxAccuracyM: maxAcceptableAccuracyM,
          })
        ) {
          return cachedPosition;
        }
      } catch (error) {
        if (getGeolocationErrorKind(error) === "permission_denied") {
          throw createGeoError("permission_denied", {
            code: error?.code,
            source: "native",
            env,
            originalError: error,
          });
        }
      }
    }

    try {
      const position = await getCurrentPosition({
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: Number(options.preciseTimeoutMs || 12000),
      });
      return normalizeNativePosition(position, env);
    } catch (error) {
      throw createGeoError(getGeolocationErrorKind(error), {
        code: error?.code,
        source: "native",
        env,
        originalError: error,
      });
    }
  }

  const preferTelegram =
    options.preferTelegram !== false && env.hasTelegramLocationManager;
  let telegramError = null;

  if (preferTelegram) {
    try {
      return await requestTelegramLocation({
        timeoutMs: options.telegramTimeoutMs,
        env,
      });
    } catch (error) {
      telegramError = error;
    }
  }

  try {
    return await requestBrowserLocation({ ...options, env });
  } catch (browserError) {
    if (telegramError) {
      const browserKind = getGeolocationErrorKind(browserError);
      if (browserKind === "not_supported" || browserKind === "insecure_context") {
        throw telegramError;
      }
    }
    throw browserError;
  }
}

export function openGeolocationSettings() {
  const webApp = getTelegramWebApp();
  const manager = webApp?.LocationManager;
  if (!telegramLocationSupported(webApp) || !manager) return false;
  try {
    manager.openSettings();
    return true;
  } catch {
    return false;
  }
}

export function getGeolocationErrorKind(error) {
  if (!error) return "unknown";
  if (error.issue) return error.issue;

  if (error.code === "OS-PLUG-GLOC-0003" || error.code === "OS-PLUG-GLOC-0008") {
    return "permission_denied";
  }
  if (error.code === "OS-PLUG-GLOC-0010") return "timeout";
  if ([
    "OS-PLUG-GLOC-0002",
    "OS-PLUG-GLOC-0007",
    "OS-PLUG-GLOC-0009",
    "OS-PLUG-GLOC-0014",
    "OS-PLUG-GLOC-0015",
    "OS-PLUG-GLOC-0016",
    "OS-PLUG-GLOC-0017",
  ].includes(error.code)) {
    return "position_unavailable";
  }

  switch (error.code) {
    case GEO_PERMISSION_DENIED:
      return "permission_denied";
    case GEO_POSITION_UNAVAILABLE:
      return "position_unavailable";
    case GEO_TIMEOUT:
      return "timeout";
    case "insecure_context":
      return "insecure_context";
    case "telegram_permission_denied":
      return "telegram_permission_denied";
    case "telegram_unavailable":
      return "telegram_unavailable";
    case "telegram_timeout":
      return "timeout";
    case "telegram_not_supported":
      return "not_supported";
    default:
      return "unknown";
  }
}
